import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAlert } from "react-alert";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import FroalaEditor from "react-froala-wysiwyg";
import withFroalaLicense from "../../../utils/froalaConfig";
import apiClient from "../../../utils/apiClient";
import Loader from "../../Loader";
import "../RichTextEditor.css";

const DEFAULT_SUBJECT = "Help us improve One Time Success — quick survey";

const DEFAULT_MESSAGE = `<p>Hi {{first_name}},</p>
<p>We would love your feedback as a One Time Success member.</p>
<p>Please take our short survey (3–5 minutes). <strong>No login is required.</strong></p>
<p><a href="{{survey_url}}">Take the survey</a></p>
<p>Thank you,<br/>One Time Success Team</p>`;

const EmailCampaign = () => {
  const alert = useAlert();
  const [audience, setAudience] = useState("active_students");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [recipientCount, setRecipientCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadAudience = async () => {
      setLoadingCount(true);
      try {
        const { data } = await apiClient.get("/admin/campaigns/email/audience", {
          params: { audience },
        });
        if (!cancelled) {
          setRecipientCount(data?.count || 0);
        }
      } catch (error) {
        if (!cancelled) {
          setRecipientCount(0);
          alert.error("Could not load recipient count.");
        }
      } finally {
        if (!cancelled) {
          setLoadingCount(false);
        }
      }
    };

    loadAudience();
    return () => {
      cancelled = true;
    };
  }, [audience, alert]);

  const handleSend = () => {
    if (!subject.trim()) {
      alert.error("Please enter an email subject.");
      return;
    }

    const stripped = message.replace(/<p><br><\/p>/gi, "").replace(/<[^>]+>/g, "").trim();
    if (!stripped) {
      alert.error("Please enter an email message.");
      return;
    }

    if (!recipientCount) {
      alert.error("No recipients found for the selected audience.");
      return;
    }

    confirmAlert({
      title: "Send email campaign",
      message: `Send this email to ${recipientCount} registered member(s)?`,
      buttons: [
        {
          label: "Send",
          onClick: async () => {
            setSending(true);
            try {
              const { data } = await apiClient.post("/admin/campaigns/email/send", {
                audience,
                subject,
                message,
              });
              alert.success(data?.message || "Campaign queued successfully.");
            } catch (error) {
              const payload = error?.response?.data;
              const firstError =
                payload?.message ||
                payload?.subject ||
                payload?.message ||
                "Failed to queue the email campaign.";
              alert.error(firstError);
            } finally {
              setSending(false);
            }
          },
        },
        { label: "Cancel" },
      ],
    });
  };

  return (
    <Fragment>
      {sending ? (
        <Loader />
      ) : (
        <div className="content-wrapper">
          <section className="content-header">
            <h1>
              Email
              <small> Member Campaign</small>
            </h1>
            <ol className="breadcrumb">
              <li>
                <Link to="/admin">
                  <i className="fa fa-dashboard"></i> Dashboard
                </Link>
              </li>
              <li className="active">Email Campaign</li>
            </ol>
          </section>

          <section className="content">
            <div className="box box-primary">
              <div className="box-header with-border">
                <h3 className="box-title">Send email to registered members</h3>
              </div>
              <div className="box-body">
                <p className="text-muted">
                  Uses your existing SMTP email engine and background queue. Personalize with{" "}
                  <code>{"{{first_name}}"}</code>, <code>{"{{full_name}}"}</code>, <code>{"{{email}}"}</code>, and{" "}
                  <code>{"{{survey_url}}"}</code>.
                </p>

                <div className="form-group">
                  <label htmlFor="campaign-audience">Audience</label>
                  <select
                    id="campaign-audience"
                    className="form-control"
                    value={audience}
                    onChange={(event) => setAudience(event.target.value)}
                  >
                    <option value="active_students">Active registered students</option>
                    <option value="all_students">All registered students</option>
                  </select>
                  <p className="help-block" style={{ marginTop: "8px" }}>
                    {loadingCount ? "Counting recipients..." : `${recipientCount} recipient(s) with a valid email.`}
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="campaign-subject">Subject</label>
                  <input
                    id="campaign-subject"
                    type="text"
                    className="form-control"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <FroalaEditor
                    tag="textarea"
                    config={withFroalaLicense({
                      placeholderText: "Write your campaign email...",
                      heightMin: 260,
                    })}
                    model={message}
                    onModelChange={setMessage}
                  />
                </div>
              </div>
              <div className="box-footer">
                <button type="button" className="btn btn-success" onClick={handleSend} disabled={loadingCount || !recipientCount}>
                  <i className="fa fa-paper-plane"></i> Queue email to {recipientCount || 0} member(s)
                </button>
                <Link to="/admin/students" className="btn btn-default" style={{ marginLeft: "8px" }}>
                  Back to students
                </Link>
              </div>
            </div>
          </section>
        </div>
      )}
    </Fragment>
  );
};

export default EmailCampaign;
